/**
 * Tatbikat bulguları ve düzeltici faaliyetler, kişi değerlendirmesi, Crew Competency Matrix ve
 * Crew ▸ Training metinleri (ADR 0128 Faz 4 Ek). Ortağın belgesindeki adlar İngilizcede aynen
 * (Equipment Defect, SMS Non-Conformity, Work Order, Training Need; Satisfactory / Needs
 * Improvement). Hata anahtarları sunucunun kodlarıyla eşleşir.
 */

export const drillFindingEn = {
  title: 'Findings & Corrective Actions',
  hint: 'Each finding opens a real record in its own module and stays linked to this drill. A non-conformity is never raised automatically: the Master classifies it.',
  types: {
    equipment_defect: 'Equipment Defect',
    sms_nc: 'SMS Non-Conformity',
    work_order: 'Work Order',
    training_need: 'Training Need',
  },
  sources: {
    preparation: 'Preparation',
    step: 'Execution step',
    evaluation: 'Evaluation',
    crew: 'Crew performance',
    manual: 'Manual',
  },
  status: {
    open: 'Open',
    in_progress: 'In Progress',
    closed: 'Closed',
    cancelled: 'Cancelled',
  },
  overdue: 'Overdue',
  columns: {
    no: 'Finding No.',
    type: 'Type',
    description: 'Description',
    responsible: 'Responsible',
    due: 'Target Date',
    linked: 'Linked Record',
    status: 'Status',
    drill: 'Drill',
    vessel: 'Vessel',
    person: 'Crew member',
    actions: 'Actions',
  },
  suggestions: 'Suggested findings',
  suggestionsHint:
    'From Needs Improvement, Not Completed and equipment issues. Nothing is raised until you choose.',
  noSuggestions: 'No open suggestions.',
  unraised_one:
    '{{count}} Needs Improvement or Not Completed item has no finding yet. Approval is not blocked.',
  unraised_other:
    '{{count}} Needs Improvement or Not Completed items have no finding yet. Approval is not blocked.',
  openCount_one: '{{count}} open finding',
  openCount_other: '{{count}} open findings',
  approveNote:
    'Open findings do not block the Master’s approval; they are followed to closure in their own module.',
  empty: 'No findings recorded.',
  actions: {
    add: 'Add Finding',
    raise: 'Raise Finding',
    edit: 'Edit',
    cancel: 'Cancel Finding',
    review: 'Review',
    openRecord: 'Open',
    open: {
      equipment_defect: 'Open Defect',
      sms_nc: 'Open Non-Conformity',
      work_order: 'Open Work Order',
      training_need: 'Open Training Need',
    },
  },
  form: {
    titleNew: 'New finding',
    titleEdit: 'Edit finding {{no}}',
    type: 'Finding type',
    description: 'Description',
    responsible: 'Responsible person',
    due: 'Target date',
    redrill: 'Repeat the drill when closed',
    redrillHint:
      'A repeat-drill suggestion appears in the Planner once the linked record is closed.',
    person: 'Crew member',
    ncNote: 'Only the Master can classify a finding as an SMS Non-Conformity.',
    linkedNote: 'The type and crew member are fixed once the linked record is open.',
    save: 'Save',
    close: 'Close',
  },
  openDialog: {
    title: '{{type}}: {{no}}',
    defectHint:
      'The defect report opens with this finding in Part A. Part B stays with the Master.',
    equipment: 'Equipment',
    noEquipment: 'No equipment — enter the system name',
    system: 'System name',
    severity: 'Severity',
    severities: { minor: 'Minor', major: 'Major', critical: 'Critical' },
    workOrderHint:
      'An unplanned work order opens for the equipment and is assigned to the responsible person.',
    target: 'Target date',
    ncHint: 'A draft non-conformity report opens in HSEQ. Complete and submit it there.',
    trainingHint: 'A training need opens for {{name}} in Crew ▸ Training.',
    confirm: 'Open Record',
  },
  cancelDialog: {
    title: 'Cancel finding {{no}}',
    reason: 'Reason',
    confirm: 'Cancel Finding',
  },
  review: {
    title: 'Review finding {{no}}',
    hint: 'Office review (DPA or Superintendent). It does not change the vessel’s record.',
    assessment: 'Assessment',
    adequate: 'Corrective action adequate',
    further_action: 'Further action required',
    comment: 'Comment',
    confirm: 'Save Review',
    reviewed: 'Office review',
  },
  chain: 'Defect → Work Order → Requisition → Verification',
  workOrders: 'Work orders',
  requisitions: 'Requisitions',
  source: 'Source: Drill {{no}}',
  sourceFinding: 'Finding {{no}}',
  dashboard: {
    kpi: 'Open Findings',
    panel: 'Open Findings & Corrective Actions',
    none: 'No open findings.',
    overdue_one: '{{count}} overdue',
    overdue_other: '{{count}} overdue',
  },
  redrill: {
    title: 'Repeat drill suggestions',
    hint: 'The finding asked for a repeat drill and its linked record is closed. No approved drill of this type has been recorded since.',
    from: '{{finding}} — {{link}} closed',
    open: 'Open Plan',
  },
  evaluation: {
    crewTitle: 'Crew Performance',
    crewHint: 'Individual assessment of the crew members present (Crew Competency Matrix).',
    person: 'Crew member',
    rating: 'Assessment',
    note: 'Observation',
    nonepresent: 'Mark the attendance in Participants first.',
    score: 'Score: {{percent}}% ({{satisfactory}} of {{rated}} Satisfactory)',
    scoreNote: 'Company method, not a regulatory requirement.',
  },
  competency: {
    description:
      'Participation, role and assessment by person and drill, from Master-approved records. Every crew member takes part in a fire and an abandon ship drill each month (SOLAS III/19.3.2).',
    vessel: 'Vessel',
    person: 'Crew member',
    lastDrill: 'Last drill',
    missing: 'Missing mandatory drill',
    trainingNeeds: 'Open training needs',
    notYet: 'Not yet',
    due: 'Due {{date}}',
    times_one: '{{count}} time',
    times_other: '{{count}} times',
    mandatory: 'Mandatory for every crew member',
    people: 'Crew on board',
    peopleMissing: 'With a missing drill',
    openNeeds: 'Open training needs',
    empty: 'No crew on board.',
    noDrills: 'No drills in the plan.',
    error: 'The competency matrix could not be loaded.',
  },
  training: {
    description:
      'Training needs raised from drill findings or entered manually; each one is closed with how it was met.',
    columns: {
      person: 'Crew member',
      topic: 'Topic',
      reason: 'Reason',
      source: 'Source',
      due: 'Target Date',
      status: 'Status',
      completed: 'Completed',
    },
    status: { open: 'Open', completed: 'Completed', cancelled: 'Cancelled' },
    manual: 'Manual',
    add: 'Add Training Need',
    complete: 'Mark Completed',
    cancel: 'Cancel',
    form: {
      title: 'New training need',
      person: 'Crew member',
      topic: 'Topic',
      reason: 'Reason',
      due: 'Target date',
      completedOn: 'Completed on',
      note: 'How it was met',
      completeTitle: 'Complete: {{topic}}',
      cancelTitle: 'Cancel: {{topic}}',
      cancelReason: 'Reason',
      save: 'Save',
    },
    empty: 'No training needs.',
    error: 'Training needs could not be loaded.',
    filterAll: 'All',
    filterOpen: 'Open',
  },
  errors: {
    INVALID_DRILL_FINDING: 'Check the finding.',
    LINK_FIXED: 'The linked record is already set.',
    FINDING_LOCKED: 'A cancelled finding cannot be changed.',
    DRILL_FINDING_FIXED: 'This field is fixed when the finding is raised.',
    REVIEWER_REQUIRED: 'Only the DPA or a Superintendent can review findings.',
    INVALID_TRAINING_NEED: 'Check the training need.',
    TRAINING_NEED_LOCKED: 'A completed or cancelled training need cannot be changed.',
    TRAINING_NEED_FIXED: 'The crew member and the source cannot be changed.',
    INVALID_WORK_ORDER: 'Select the equipment and a target date.',
    INVALID_DEFECT: 'Select the equipment or enter the system name.',
    VALIDATION_FAILED: 'Check the entered values.',
  },
};

export const drillFindingTr: typeof drillFindingEn = {
  title: 'Bulgular ve Düzeltici Faaliyetler',
  hint: 'Her bulgu kendi modülünde gerçek bir kayıt açar ve bu tatbikata bağlı kalır. Uygunsuzluk kendiliğinden açılmaz: kaptan sınıflar.',
  types: {
    equipment_defect: 'Ekipman Arızası',
    sms_nc: 'SMS Uygunsuzluğu',
    work_order: 'İş Emri',
    training_need: 'Eğitim İhtiyacı',
  },
  sources: {
    preparation: 'Hazırlık',
    step: 'İcra adımı',
    evaluation: 'Değerlendirme',
    crew: 'Kişi değerlendirmesi',
    manual: 'Elle',
  },
  status: {
    open: 'Açık',
    in_progress: 'Sürüyor',
    closed: 'Kapandı',
    cancelled: 'İptal',
  },
  overdue: 'Gecikmiş',
  columns: {
    no: 'Bulgu No.',
    type: 'Tür',
    description: 'Açıklama',
    responsible: 'Sorumlu',
    due: 'Hedef Tarih',
    linked: 'Bağlı Kayıt',
    status: 'Durum',
    drill: 'Tatbikat',
    vessel: 'Gemi',
    person: 'Kişi',
    actions: 'İşlemler',
  },
  suggestions: 'Önerilen bulgular',
  suggestionsHint:
    'Geliştirilmeli, Tamamlanmadı ve ekipman sorunlarından. Siz seçmeden bulgu açılmaz.',
  noSuggestions: 'Açık öneri yok.',
  unraised_one:
    'Geliştirilmeli ya da Tamamlanmadı {{count}} maddenin bulgusu henüz yok. Onay engellenmez.',
  unraised_other:
    'Geliştirilmeli ya da Tamamlanmadı {{count}} maddenin bulgusu henüz yok. Onay engellenmez.',
  openCount_one: '{{count}} açık bulgu',
  openCount_other: '{{count}} açık bulgu',
  approveNote:
    'Açık bulgu kaptan onayını engellemez; bulgular kendi modüllerinde kapanışa kadar izlenir.',
  empty: 'Bulgu kaydı yok.',
  actions: {
    add: 'Bulgu Ekle',
    raise: 'Bulgu Aç',
    edit: 'Düzenle',
    cancel: 'Bulguyu İptal Et',
    review: 'Değerlendir',
    openRecord: 'Aç',
    open: {
      equipment_defect: 'Arıza Aç',
      sms_nc: 'Uygunsuzluk Aç',
      work_order: 'İş Emri Aç',
      training_need: 'Eğitim İhtiyacı Aç',
    },
  },
  form: {
    titleNew: 'Yeni bulgu',
    titleEdit: '{{no}} bulgusunu düzenle',
    type: 'Bulgu türü',
    description: 'Açıklama',
    responsible: 'Sorumlu kişi',
    due: 'Hedef tarih',
    redrill: 'Kapanınca tatbikatı tekrarla',
    redrillHint: 'Bağlı kayıt kapanınca Planner’da tekrar tatbikat önerisi çıkar.',
    person: 'Kişi',
    ncNote: 'Bulguyu SMS uygunsuzluğu olarak yalnız kaptan sınıflar.',
    linkedNote: 'Bağlı kayıt açıldıktan sonra tür ve kişi değişmez.',
    save: 'Kaydet',
    close: 'Kapat',
  },
  openDialog: {
    title: '{{type}}: {{no}}',
    defectHint: 'Arıza raporu bu bulguyla Part A’da açılır. Part B kaptanındır.',
    equipment: 'Ekipman',
    noEquipment: 'Ekipman yok — sistem adını yazın',
    system: 'Sistem adı',
    severity: 'Ciddiyet',
    severities: { minor: 'Küçük', major: 'Büyük', critical: 'Kritik' },
    workOrderHint: 'Ekipman için plansız iş emri açılır ve sorumlu kişiye atanır.',
    target: 'Hedef tarih',
    ncHint: 'HSEQ’de taslak uygunsuzluk raporu açılır. Orada tamamlayıp gönderin.',
    trainingHint: '{{name}} için Crew ▸ Training’de eğitim ihtiyacı açılır.',
    confirm: 'Kaydı Aç',
  },
  cancelDialog: {
    title: '{{no}} bulgusunu iptal et',
    reason: 'Gerekçe',
    confirm: 'Bulguyu İptal Et',
  },
  review: {
    title: '{{no}} bulgusunu değerlendir',
    hint: 'Ofis değerlendirmesi (DPA ya da Superintendent). Geminin kaydını değiştirmez.',
    assessment: 'Değerlendirme',
    adequate: 'Düzeltici faaliyet yeterli',
    further_action: 'Ek faaliyet gerekli',
    comment: 'Yorum',
    confirm: 'Değerlendirmeyi Kaydet',
    reviewed: 'Ofis değerlendirmesi',
  },
  chain: 'Arıza → İş Emri → Talep → Doğrulama',
  workOrders: 'İş emirleri',
  requisitions: 'Talepler',
  source: 'Kaynak: Tatbikat {{no}}',
  sourceFinding: 'Bulgu {{no}}',
  dashboard: {
    kpi: 'Açık Bulgular',
    panel: 'Açık Bulgular ve Düzeltici Faaliyetler',
    none: 'Açık bulgu yok.',
    overdue_one: '{{count}} gecikmiş',
    overdue_other: '{{count}} gecikmiş',
  },
  redrill: {
    title: 'Tekrar tatbikat önerileri',
    hint: 'Bulgu tekrar tatbikat istedi ve bağlı kaydı kapandı. O zamandan beri bu türde onaylı tatbikat kaydı yok.',
    from: '{{finding}} — {{link}} kapandı',
    open: 'Planı Aç',
  },
  evaluation: {
    crewTitle: 'Kişi Değerlendirmesi',
    crewHint: 'Katılan kişilerin tek tek değerlendirmesi (Crew Competency Matrix).',
    person: 'Kişi',
    rating: 'Değerlendirme',
    note: 'Gözlem',
    nonepresent: 'Önce Participants’ta katılımı işaretleyin.',
    score: 'Puan: %{{percent}} ({{rated}} kriterden {{satisfactory}} Yeterli)',
    scoreNote: 'Şirket içi yöntem; mevzuat zorunluluğu değildir.',
  },
  competency: {
    description:
      'Kaptan onaylı kayıtlardan kişi ve tatbikat bazında katılım, rol ve değerlendirme. Her mürettebat her ay bir yangın ve bir gemiyi terk tatbikatına katılır (SOLAS III/19.3.2).',
    vessel: 'Gemi',
    person: 'Kişi',
    lastDrill: 'Son tatbikat',
    missing: 'Eksik zorunlu tatbikat',
    trainingNeeds: 'Açık eğitim ihtiyacı',
    notYet: 'Henüz yok',
    due: 'Son gün {{date}}',
    times_one: '{{count}} kez',
    times_other: '{{count}} kez',
    mandatory: 'Her mürettebat için zorunlu',
    people: 'Gemideki kişi',
    peopleMissing: 'Eksik tatbikatı olan',
    openNeeds: 'Açık eğitim ihtiyacı',
    empty: 'Gemide kişi yok.',
    noDrills: 'Planda tatbikat yok.',
    error: 'Yetkinlik matrisi yüklenemedi.',
  },
  training: {
    description:
      'Tatbikat bulgularından açılan ya da elle girilen eğitim ihtiyaçları; her biri nasıl karşılandığıyla kapanır.',
    columns: {
      person: 'Kişi',
      topic: 'Konu',
      reason: 'Gerekçe',
      source: 'Kaynak',
      due: 'Hedef Tarih',
      status: 'Durum',
      completed: 'Tamamlandı',
    },
    status: { open: 'Açık', completed: 'Tamamlandı', cancelled: 'İptal' },
    manual: 'Elle',
    add: 'Eğitim İhtiyacı Ekle',
    complete: 'Tamamlandı Olarak İşaretle',
    cancel: 'İptal Et',
    form: {
      title: 'Yeni eğitim ihtiyacı',
      person: 'Kişi',
      topic: 'Konu',
      reason: 'Gerekçe',
      due: 'Hedef tarih',
      completedOn: 'Tamamlanma günü',
      note: 'Nasıl karşılandı',
      completeTitle: 'Tamamla: {{topic}}',
      cancelTitle: 'İptal: {{topic}}',
      cancelReason: 'Gerekçe',
      save: 'Kaydet',
    },
    empty: 'Eğitim ihtiyacı yok.',
    error: 'Eğitim ihtiyaçları yüklenemedi.',
    filterAll: 'Tümü',
    filterOpen: 'Açık',
  },
  errors: {
    INVALID_DRILL_FINDING: 'Bulguyu kontrol edin.',
    LINK_FIXED: 'Bağlı kayıt zaten kurulu.',
    FINDING_LOCKED: 'İptal edilmiş bulgu değiştirilemez.',
    DRILL_FINDING_FIXED: 'Bu alan bulgu açılırken sabitlenir.',
    REVIEWER_REQUIRED: 'Bulguyu yalnız DPA ya da Superintendent değerlendirir.',
    INVALID_TRAINING_NEED: 'Eğitim ihtiyacını kontrol edin.',
    TRAINING_NEED_LOCKED: 'Tamamlanmış ya da iptal edilmiş eğitim ihtiyacı değiştirilemez.',
    TRAINING_NEED_FIXED: 'Kişi ve kaynak değiştirilemez.',
    INVALID_WORK_ORDER: 'Ekipmanı ve hedef tarihi seçin.',
    INVALID_DEFECT: 'Ekipmanı seçin ya da sistem adını yazın.',
    VALIDATION_FAILED: 'Girilen değerleri kontrol edin.',
  },
};
